import React, { useState, useEffect } from 'react';
import { BrainCircuit, Loader2, Eye, EyeOff, FlaskConical, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AITab() {
  const [config, setConfig] = useState({ provider: 'gemini', has_custom_key: false, using_emergent: true });
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/ai`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/ai`, { provider: config.provider, api_key: apiKey });
      toast.success('Impostazioni AI salvate');
      setApiKey('');
      const updated = await axios.get(`${API}/settings/ai`);
      setConfig(updated.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleRemoveKey = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/ai`, { provider: config.provider, api_key: '' });
      toast.success('Chiave rimossa — verrà usata la chiave Emergent');
      const updated = await axios.get(`${API}/settings/ai`);
      setConfig(updated.data);
    } catch { toast.error('Errore'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!apiKey) { toast.error('Inserisci prima la chiave API'); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/settings/ai/test`, { provider: config.provider, api_key: apiKey });
      toast.success('Chiave valida! Connessione AI funzionante.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chiave non valida');
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-xl border" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: config.has_custom_key ? '#E4F7EE' : '#EDE8FF' }}>
            <BrainCircuit size={15} style={{ color: config.has_custom_key ? '#2DAA7E' : '#6B6FD8' }} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">
              {config.has_custom_key ? 'Chiave personale attiva' : 'Usando chiave Emergent'}
            </p>
            <p className="text-xs text-gray-500">
              {config.has_custom_key
                ? `Provider: ${config.provider === 'openai' ? 'OpenAI (GPT-4o mini)' : 'Google (Gemini Flash)'}`
                : 'La categorizzazione AI e la traduzione usano la chiave condivisa Emergent'}
            </p>
          </div>
          {config.has_custom_key && (
            <button onClick={handleRemoveKey} disabled={saving}
              className="ml-auto text-xs text-red-400 hover:text-red-600 transition-colors">
              Rimuovi
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Provider AI</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'gemini', label: 'Google Gemini', sub: 'gemini-3-flash-preview', color: '#4285F4' },
            { id: 'openai', label: 'OpenAI',        sub: 'gpt-4o-mini',           color: '#10A37F' },
          ].map(p => (
            <button key={p.id} onClick={() => setConfig(c => ({ ...c, provider: p.id }))}
              data-testid={`ai-provider-${p.id}`}
              className="flex items-center gap-3 p-3 rounded-xl border text-left transition-all"
              style={{
                borderColor: config.provider === p.id ? p.color : 'hsl(var(--border))',
                background: config.provider === p.id ? `${p.color}10` : 'white',
                boxShadow: config.provider === p.id ? `0 0 0 2px ${p.color}33` : 'none',
              }}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
              <div>
                <div className="text-sm font-medium text-gray-800">{p.label}</div>
                <div className="text-xs text-gray-400">{p.sub}</div>
              </div>
              {config.provider === p.id && <Check size={14} className="ml-auto" style={{ color: p.color }} />}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chiave API {config.provider === 'openai' ? 'OpenAI' : 'Google AI'}
        </label>
        <div className="relative">
          <input
            data-testid="ai-api-key-input"
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder={config.has_custom_key ? '••••••••••••••••• (chiave già salvata)' : 'Incolla qui la tua chiave API...'}
            className="w-full px-4 py-3 pr-10 text-sm border rounded-xl focus:outline-none transition-all"
            style={{ borderColor: 'hsl(var(--border))' }}
            onFocus={e => e.target.style.borderColor = '#E2725B'}
            onBlur={e => e.target.style.borderColor = 'hsl(var(--border))'}
          />
          <button type="button" onClick={() => setShowKey(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          {config.provider === 'openai'
            ? 'Ottieni la chiave su platform.openai.com → API Keys'
            : 'Ottieni la chiave su aistudio.google.com → Get API Key'}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid="ai-test-btn"
          onClick={handleTest}
          disabled={testing || !apiKey}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border rounded-xl transition-all hover:bg-accent"
          style={{ borderColor: 'hsl(var(--border))', opacity: !apiKey ? 0.5 : 1 }}
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
          {testing ? 'Test in corso...' : 'Testa chiave'}
        </button>
        <button
          data-testid="ai-save-btn"
          onClick={handleSave}
          disabled={saving || !apiKey}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all"
          style={{ background: '#E2725B', opacity: (saving || !apiKey) ? 0.6 : 1 }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {saving ? 'Salvataggio...' : 'Salva chiave'}
        </button>
      </div>

      <div className="p-4 rounded-xl text-xs text-gray-500 leading-relaxed"
        style={{ background: 'hsl(40,18%,96%)', border: '1px solid hsl(40,15%,88%)' }}>
        <strong className="block text-gray-700 mb-1">Come funziona</strong>
        Se inserisci una chiave personale, verrà usata <strong>al posto</strong> di quella Emergent per la
        categorizzazione AI e la traduzione delle email. La chiave è salvata cifrata nel database locale.
        Se la rimuovi, si torna automaticamente alla chiave Emergent.
      </div>
    </div>
  );
}
